import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, ChevronDown, ChevronRight } from 'lucide-react';
import Spinner from '../components/ui/Spinner';
import Pagination from '../components/ui/Pagination';
import { getAuditLogs, getAuditLogStats } from '../services/auditLogService';
import type { AuditLogItem } from '../services/auditLogService';

const ACTION_LABELS: Record<string, string> = {
  'org.plan.change': '플랜 변경',
  'org.storage_limit.change': '스토리지 한도 조정',
  'org.deactivate': '조직 비활성화',
  'org.activate': '조직 활성화',
  'bug.status.change': '오류 상태 변경',
  'bug.memo.create': '메모 작성',
  'admin.login': '로그인',
  'admin.login.failed': '로그인 실패',
  'admin.totp.enable': '2FA 활성화',
  'admin.totp.disable': '2FA 비활성화',
  'admin.ip_whitelist.update': 'IP 화이트리스트 변경',
};

const ACTION_OPTIONS = [
  { value: '', label: '전체 액션' },
  { value: 'org.plan.change', label: '플랜 변경' },
  { value: 'org.storage_limit.change', label: '스토리지 한도 조정' },
  { value: 'org.deactivate', label: '조직 비활성화' },
  { value: 'org.activate', label: '조직 활성화' },
  { value: 'bug.status.change', label: '오류 상태 변경' },
  { value: 'bug.memo.create', label: '메모 작성' },
  { value: 'admin.login', label: '로그인' },
  { value: 'admin.login.failed', label: '로그인 실패' },
];

const TARGET_TYPE_OPTIONS = [
  { value: '', label: '전체 대상' },
  { value: 'Organization', label: 'Organization' },
  { value: 'BugReport', label: 'BugReport' },
  { value: 'User', label: 'User' },
  { value: 'System', label: 'System' },
];

function formatTime(iso: string): string {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function formatFull(iso: string): string {
  return new Date(iso).toLocaleString('ko-KR', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export default function AuditLogsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [action, setAction] = useState('');
  const [targetType, setTargetType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<{
    action: string; targetType: string; from: string; to: string; search: string;
  }>({ action: '', targetType: '', from: '', to: '', search: '' });
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const applyFilters = () => {
    setAppliedFilters({ action, targetType, from: fromDate, to: toDate, search });
    setPage(1);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'audit-logs', { page, ...appliedFilters }],
    queryFn: () => getAuditLogs({
      page,
      limit: 50,
      action: appliedFilters.action || undefined,
      targetType: appliedFilters.targetType || undefined,
      from: appliedFilters.from || undefined,
      to: appliedFilters.to || undefined,
      search: appliedFilters.search || undefined,
    }),
  });

  const { data: stats } = useQuery({
    queryKey: ['admin', 'audit-logs-stats'],
    queryFn: () => getAuditLogStats(30),
  });

  const todayCount = stats?.dailyTrend
    ? stats.dailyTrend.data[stats.dailyTrend.data.length - 1] ?? 0
    : 0;
  const topAction = stats?.byAction?.[0];

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold text-gray-900">감사 로그</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-sm text-gray-500 mb-1">총 기록수 (30일)</p>
          <p className="text-2xl font-bold text-gray-900">{stats?.totalCount?.toLocaleString() ?? '-'}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-sm text-gray-500 mb-1">오늘 기록</p>
          <p className="text-2xl font-bold text-gray-900">{todayCount}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-sm text-gray-500 mb-1">최다 액션</p>
          <p className="text-2xl font-bold text-gray-900">
            {topAction ? (ACTION_LABELS[topAction.action] ?? topAction.action) : '-'}
          </p>
          {topAction && <p className="text-sm text-gray-400 mt-0.5">{topAction.count}건</p>}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <select value={action} onChange={(e) => setAction(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-primary-500">
            {ACTION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={targetType} onChange={(e) => setTargetType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-primary-500">
            {TARGET_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-primary-500" />
          <span className="text-gray-400 text-sm">~</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-primary-500" />
          <div className="relative flex-1 min-w-[160px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="검색어 입력..."
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <button onClick={applyFilters}
            className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors">
            필터 적용
          </button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner className="size-8" /></div>
      ) : !data || data.items.length === 0 ? (
        <p className="text-center py-16 text-sm text-gray-400">감사 로그가 없습니다.</p>
      ) : (
        <>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase w-8"></th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">시간</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">관리자</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">액션</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">대상</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.items.map((log) => (
                  <LogRow key={log.id} log={log} expanded={expandedId === log.id}
                    onToggle={() => setExpandedId(expandedId === log.id ? null : log.id)}
                    navigate={navigate} />
                ))}
              </tbody>
            </table>
          </div>

          {data.pagination.totalPages > 1 && (
            <Pagination page={page} totalPages={data.pagination.totalPages} onPageChange={setPage} />
          )}
        </>
      )}
    </div>
  );
}

function LogRow({ log, expanded, onToggle, navigate }: {
  log: AuditLogItem; expanded: boolean;
  onToggle: () => void; navigate: ReturnType<typeof useNavigate>;
}) {
  const actionLabel = ACTION_LABELS[log.action] ?? log.action;
  const displayTarget = log.targetName ?? (log.targetId ? log.targetId.slice(0, 8) : '-');

  const isLoginFailed = log.action === 'admin.login.failed';

  const handleTargetClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!log.targetId) return;
    if (log.targetType === 'Organization') navigate(`/organizations/${log.targetId}`);
    else if (log.targetType === 'BugReport') navigate(`/bug-reports/${log.targetId}`);
  };

  return (
    <>
      <tr onClick={onToggle} className="hover:bg-gray-50 cursor-pointer">
        <td className="px-4 py-3 text-gray-400">
          {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </td>
        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatTime(log.createdAt)}</td>
        <td className="px-4 py-3 text-sm text-gray-900">{log.actorName}</td>
        <td className="px-4 py-3">
          <span className={`inline-block px-2 py-0.5 text-sm font-medium rounded-full ${
            isLoginFailed ? 'bg-red-50 text-red-700' :
            log.action.startsWith('org.') ? 'bg-blue-50 text-blue-700' :
            log.action.startsWith('bug.') ? 'bg-amber-50 text-amber-700' :
            'bg-gray-100 text-gray-700'
          }`}>
            {actionLabel}
          </span>
        </td>
        <td className="px-4 py-3 text-sm">
          {log.targetId && (log.targetType === 'Organization' || log.targetType === 'BugReport') ? (
            <button onClick={handleTargetClick}
              className="text-primary-600 hover:underline">{displayTarget}</button>
          ) : (
            <span className="text-gray-600">{displayTarget}</span>
          )}
        </td>
        <td className="px-4 py-3 text-sm text-gray-500 font-mono text-sm">{log.ipAddress ?? '-'}</td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={6} className="bg-gray-50 px-6 py-4">
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm mb-3">
              <Detail label="시간" value={formatFull(log.createdAt)} />
              <Detail label="관리자" value={log.actorEmail ? `${log.actorName} (${log.actorEmail})` : log.actorName} />
              <Detail label="액션" value={`${log.action} (${actionLabel})`} />
              <Detail label="대상" value={`${log.targetType}${log.targetName ? ` — ${log.targetName}` : ''}`} />
              <Detail label="IP" value={log.ipAddress ?? '-'} />
            </div>
            {log.detail && (
              <div>
                <p className="text-sm text-gray-500 mb-1">변경 상세</p>
                <pre className="bg-gray-100 rounded-lg p-3 text-sm font-mono text-gray-800 overflow-x-auto">
                  {JSON.stringify(log.detail, null, 2)}
                </pre>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-gray-500">{label}: </span>
      <span className="text-gray-900 font-medium">{value}</span>
    </div>
  );
}
